import '@babel/polyfill';
import React from 'react';
import ReactDOM from 'react-dom';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import i18n, { t } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import AppContainer from './components/app-container';
import BLOCKv from './util/blockv';
import Loader from './components/loader';
import AddAccountStep from './features/add-account';
import UpdateDetails from './features/update-details';
import LoginPasswordStep from './features/login-password';
import LoginOtpStep from './features/login-otp';
import CountrySelect from './features/country-select';
import AlertProvider from './util/alert/alert-provider';
import Alert from './util/alert';
import Countries from './util/countries';
import getAllUrlParams from './util/get-url-params';
import LocalEn from './locales/en.json';

const pathPrefix = '.'; //"https://s3.amazonaws.com/login.blockv.io/vn/2.0.3";
const host = 'login.blockv.io';
const server = 'https://api.bv.vatom.network';

/** Called on app startup */
document.addEventListener('DOMContentLoaded', function () {
  // Render React app
  var div = document.createElement('div');
  document.body.appendChild(div);
  ReactDOM.render(<App />, div);
});

const defaultConfig = {
  app: {
    logo: null,
    disable_email: false,
    disable_phone: false,
    region: null,
    enforce_region: false,
  },
  register: {
    skip_capture: false,
  },
  details: {
    disable_name: true,
    disable_password: false,
    disable_birthday: true,
    enforce: false,
  },
};
/** Steps in the login process */
const Steps = {
  /** This step we allow the user to enter their token to add an account. This is the start of the login flow. */
  AddAccount: 0,

  /** This step we allow the user to create an account for themselves */
  CreateAccount: 1,

  /** This step we login with the user's password. */
  LoginPassword: 2,

  /** Similar to LoginPassword, on this step we login with an OTP instead. */
  LoginOTP: 3,

  /** This step we allow the user to  update required account details */
  UpdateAccount: 4,
};
const supportedParams = [
  'platform',
  'client_id',
  'redirect_uri',
  'state',
  'no_padding',
  'first_name',
  'last_name',
  'token',
  'disable_email',
  'disable_phone',
  'region',
  'enforce_region',
  'skip_register_capture',
  'skip_capture_name',
  'skip_capture_password',
  'skip_capture_birthday',
  'enforce_login_capture',
];

const platforms = {
  blockv: {
    api: 'https://api.bv.vatom.network',
    websocket: 'wss://newws.blockv.io/ws',
  },
  vatominc: {
    api: 'https://api.vi.vatom.network',
    websocket: 'wss://ws.vatominc.net/ws',
  },
  vatomlabs: {
    api: 'https://api.vl.vatom.network',
    websocket: 'wss://ws.vatomviewer.com/ws',
  },
};
/** Main app component */
class App extends React.Component {
  constructor(props) {
    super(props);

    // Setup state
    this.state = {};
    this.state.step = 0;
    this.state.tokens = [];
    this.state.selectedCountry = {
      code: 'us',
      phonePrefix: '+1',
      name: 'United States',
    };
    this.state.loadingText = t('general.loader_loading');
    this.state.flow = 'login';

    // Login form state
    this.state.loginToken = '';
    this.state.loginTokenCleaned = '';
    this.state.loginPassword = '';

    // The list of available user sessions
    this.state.availableSessions = [];

    // The currently selected user session
    this.state.currentSession = null;

    // True if the user went to the Select Account screen
    this.didSelectAccount = false;

    // Pull required details from the URL
    const params = getAllUrlParams();

    this.clientID = params['client_id'];
    this.platform = params['platform'] || 'blockv';
    this.redirectURI = decodeURIComponent(params['redirect_uri']);
    this.customState = params['state'] || '';
    this.state.noPadding = params['no_padding'];
    // Prefill certain fields if necessary
    if (params['first_name']) this.state.registerFirstName = params['first_name'];
    if (params['last_name']) this.state.registerLastName = params['last_name'];
    if (params['token']) {
      this.state.loginToken = params['token'];
      this.state.loginTokenIsPhone = /^[ \(\)\-0-9]+$/.test(this.state.loginToken);
      this.loginImmediately = params['skip_token'];
    }

    // Using the PROD environment
    BLOCKv.AppID = this.clientID;
    BLOCKv.server = platforms[this.platform]?.api ?? server;
  }

  async componentDidMount() {
    // Verify fields passed in by the caller app
    if (!this.clientID || !this.redirectURI)
      return this.doFail('invalid_input', 'Some fields were missing from the url.');

    // Go to the first state
    this.fetchCurrentStatus();
  }

  render() {
    // Render app container
    return (
      <>
        <AlertProvider />
        <AppContainer noPadding={this.state.noPadding}>
          {/* Add Account (login) step */}
          <AddAccountStep
            step={Steps.AddAccount}
            currentStep={this.state.step}
            disableEmail={this.state.disableEmail}
            disablePhone={this.state.disablePhone}
            token={this.state.loginToken}
            tokenIsPhone={this.state.loginTokenIsPhone}
            onInputChange={(value) =>
              this.setState({
                addAccountError: null,
                loginToken: value,
                loginTokenIsPhone: /^[ \(\)\-0-9]+$/.test(value),
              })
            }
            countryCode={this.state.loginTokenIsPhone && this.state.selectedCountry.phonePrefix}
            onBackClick={() => this.cancelPermission()}
            onNextClick={() => this.doLogin()}
            error={this.state.addAccountError}
            onCountryCodeClick={() =>
              !this.state.enforceLocation &&
              CountrySelect.show(this.state.selectedCountry, null, (country) =>
                this.setState({ selectedCountry: country })
              )
            }
          />
          {/* Create Account (register) step */}
          <UpdateDetails
            register
            step={Steps.CreateAccount}
            currentStep={this.state.step}
            skipName={this.state.registerDisableName}
            skipPassword={this.state.registerDisablePassword}
            skipBirthday={this.state.registerDisableBirthday}
            firstName={this.state.registerFirstName}
            lastName={this.state.registerLastName}
            birthday={null}
            onBackClick={() => this.gotoAddAccount()}
            onNextClick={(info) => this.doRegister(info)}
          />
          {/* Login Password step */}
          <LoginPasswordStep
            step={Steps.LoginPassword}
            currentStep={this.state.step}
            token={this.state.loginTokenCleaned}
            onInputChange={() => this.setState({ loginPasswordError: null })}
            onBackClick={() => this.gotoAddAccount()}
            onLoginClick={(password) => this.doLoginWithPassword(password)}
            onForgotPasswordClick={() => this.doLoginForgotPassword()}
            error={this.state.loginPasswordError}
          />

          {/* Login OTP step */}
          <LoginOtpStep
            step={Steps.LoginOTP}
            currentStep={this.state.step}
            token={this.state.loginTokenCleaned}
            onResendOtpClick={() => this.doLoginResendOTP()}
            onBackClick={() => this.gotoAddAccount()}
            onLoginClick={(otp) => this.doLoginWithOTP(otp)}
            error={this.state.loginOtpError}
            onInputChange={() => this.setState({ loginOtpError: null })}
          />

          <UpdateDetails
            register={false}
            step={Steps.UpdateAccount}
            currentStep={this.state.step}
            skipName={this.state.registerDisableName}
            skipPassword={this.state.registerDisablePassword || this.state.loginPasswordSet}
            skipBirthday={this.state.registerDisableBirthday}
            firstName={this.state.loginFirstName}
            lastName={this.state.loginLastName}
            birthday={this.state.loginBirthday}
            onBackClick={() => {
              this.setState({ refreshToken: null });
              this.gotoAddAccount();
            }}
            onNextClick={(info) => this.doUpdate(info)}
          />
          {/* Check if loading */}
          <Loader show={!!this.state.loadingText} text={this.state.loadingText} />
        </AppContainer>
      </>
    );
  }

  static mapString(o) {
    return Object.keys(o)
      .map((key) => `${key}=${o[key]}`)
      .join('&');
  }

  async getAppConfig() {
    try {
      let baseURL = location.host == host ? '.' : pathPrefix;
      let response = await fetch(`${baseURL}/${this.clientID}/config.json`);
      let json_response = await response.json();
      let config = Object.assign({}, defaultConfig);
      return Object.assign(config, json_response);
    } catch (e) {
      console.warn(e);
    }
    let config = Object.assign({}, defaultConfig);
    const params = getAllUrlParams();
    config.app.disable_email = params['disable_email'];
    if (params['disable_phone'] && !config.app.disable_email)
      config.app.disable_phone = params['disable_phone'];
    if (params['region']) config.app.region = params['region'];
    config.app.enforce_region = params['enforce_region'];
    config.register.skip_capture = params['skip_register_capture'];
    config.details.disable_name = params['skip_capture_name'];
    config.details.disable_password = params['skip_capture_password'];
    config.details.disable_birthday = params['skip_capture_birthday'];
    config.details.enforce = params['enforce_login_capture'];

    return config;
  }

  async setupLanguage(path, locales) {
    if (!locales) locales = [];
    const resources = {
      en: { translation: LocalEn },
    };
    for (var i = 0; i < locales.length; i++) {
      try {
        const local = locales[i];
        resources[local] = {
          translation: await fetch(
            `${path}/${this.clientID}/resources/locales/${local}.json`
          ).then((response) => response.json()),
        };
      } catch (error) {
        console.warn(error);
      }
    }
    i18n.use(LanguageDetector).init({
      debug: true,
      fallbackLng: 'en',
      detection: {
        order: ['navigator', 'localStorage', 'querystring', 'cookie', 'htmlTag'],
      },
      resources,
    });

    i18n.on('initialized', (e) => {
      this.forceUpdate();
    });
    i18n.on('loaded', (e) => this.forceUpdate());
    i18n.on('languageChanged', (e) => this.forceUpdate());
  }

  /** This fetches the current user session status and app information */
  async fetchCurrentStatus() {
    // Show loader
    this.setState({ loadingText: t('general.loader_loading') });
    let baseURL = location.host == host ? '.' : pathPrefix;
    let url = baseURL + `/${this.clientID}/styles.css`;
    let stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.type = 'text/css';
    stylesheet.href = url;
    document.getElementsByTagName('head')[0].appendChild(stylesheet);

    let conf = await this.getAppConfig();
    await this.setupLanguage(baseURL, conf.locales);

    this.setState({
      campaignLocation: conf.app && conf.app.region,
      enforceLocation: conf.app && conf.app.enforce_region,
      applogo: conf.app && conf.app.logo,
      disableEmail: conf.app && conf.app.disable_email,
      disablePhone: conf.app && conf.app.disable_phone,
      registerSkipCapture: conf.register && conf.register.skip_capture,
      registerDisableName: conf.details && conf.details.disable_name,
      registerDisableBirthday: conf.details && conf.details.disable_birthday,
      registerDisablePassword: conf.details && conf.details.disable_password,
      enforceDetails: conf.details && conf.details.enforce,
    });

    let defaultCountry = {
      code: 'us',
      phonePrefix: '+1',
      name: 'United States',
    };
    if (conf.app && conf.app.region) {
      const country = (await Countries.get()).find(
        (country) => country.code.toLowerCase() == conf.app.region.toLowerCase()
      );
      if (country) {
        defaultCountry = country;
      }
    }
    if (conf.app && conf.app.enforce_region) {
      this.setState({
        selectedCountry: defaultCountry,
      });
    } else {
      // Try to autodetect the phone country code prefix
      Countries.detect().then((country) => {
        // Store it
        this.setState({
          selectedCountry: country || defaultCountry,
        });
      });
    }
    // Store current tokens
    this.setState({ loadingText: null });
    // No tokens, go to the Add Account screen
    this.gotoAddAccount();
  }

  /** Called to go to the Add Account step */
  gotoAddAccount() {
    // Show step
    this.setState({ step: Steps.AddAccount });

    // Check if should login immediately with the pre-specified token
    if (this.state.loginToken && this.loginImmediately) {
      // Login now
      this.loginImmediately = false;
      this.doLogin();
    }
  }

  convertDate(str) {
    const date = new Date(str);
    const mnth = `0${date.getMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);
    return [date.getFullYear(), mnth, day].join('-');
  }
  /** Called when the user has filled out the register form and wants to register their account */
  doRegister(info) {
    // Show loader
    this.setState({ loadingText: t('register-account.loader_registering') });
    const token = this.state.registerToken.trim();
    const tokenType = this.state.registerTokenType;
    const payload = {
      password: info.password,
      first_name: info.firstName,
      last_name: info.lastName,
      user_tokens: [
        {
          token,
          token_type: tokenType,
        },
      ],
    };

    if (info.birthday) {
      payload.birthday = this.convertDate(info.birthday);
    }
    // Send request
    BLOCKv.request('POST', '/v1/users', payload)
      .then((e) => {
        // Remove loader
        this.setState({ loadingText: null, flow: 'register' });
        this.setState({ loginTokenCleaned: token });
        // Go to confirm screen
        this.gotoLoginOTPStep();
        return BLOCKv.request('POST', '/v1/user/reset_token', { token, token_type: tokenType });
      })
      .catch((err) => {
        // Show error
        this.setState({ loadingText: null });
        Alert.notify(t('error.title'), err.message, t('error.action_confirm'));
      });
  }

  async doUpdate(info) {
    let payload = {};
    if (info.firstName && info.firstName.length > 0) {
      payload.first_name = info.firstName;
    }
    if (info.lastName && info.lastName.length > 0) {
      payload.last_name = info.lastName;
    }
    if (info.birthday && info.birthday !== '') {
      payload.birthday = this.convertDate(info.birthday);
    }
    if (info.password && info.password.length > 0) {
      payload.password = info.password;
    }
    try {
      this.setState({ loadingText: t('update-account.loader_updating') });
      const access = await BLOCKv.getAccessToken(this.state.refreshToken);
      await BLOCKv.request('PATCH', '/v1/user', payload, { Authorization: 'bearer ' + access });
      this.setState({ loadingText: null });
      this.doSuccess(this.state.refreshToken);
    } catch (err) {
      this.setState({ loadingText: null });
      Alert.notify(t('error.title'), err.message, t('error.action_confirm'));
    }
  }
  /** called if there is a config item to skip register */
  async doSkipRegister(token, token_type) {
    try {
      this.setState({ loadingText: t('register-account.loader_registering') });
      await BLOCKv.request('POST', '/v1/users', { user_tokens: [{ token, token_type }] });
      this.setState({ loadingText: null });
      this.setState({ loginTokenCleaned: token });
      this.gotoLoginOTPStep();
      await BLOCKv.request('POST', '/v1/user/reset_token', { token, token_type });
    } catch (err) {
      this.setState({ loadingText: null });
      Alert.notify(t('error.title'), err.message, t('error.action_confirm'));
    }
  }

  validateToken() {
    // Check if it's an email address
    var token = this.state.loginToken;
    var tokenType = '';
    if (token.indexOf('@') != -1) {
      if (this.state.disableEmail) {
        this.setState({ addAccountError: t('add-account.error_invalid_input_phone_required') });
        return {};
      }
      // Email
      tokenType = 'email';
    } else {
      if (this.state.disablePhone) {
        this.setState({ addAccountError: t('add-account.error_invalid_input_email_required') });
        return {};
      }

      // Catch phone number conversion errors
      try {
        // check for numbers only
        if (!/^[ +()\-0-9]+$/.test(token))
          throw new Error('Invalid characters found for phone number');

        // Must be a phone number, format it correctly
        var phoneUtil = PhoneNumberUtil.getInstance();
        let checkagainst = this.state && this.state.selectedCountry.code;
        var pnf = phoneUtil.parse(token, checkagainst);

        token = phoneUtil.format(pnf, PhoneNumberFormat.E164).trim();
        if (
          this.state &&
          this.state.enforceLocation &&
          !token.startsWith(this.state.selectedCountry.phonePrefix)
        ) {
          this.setState({
            addAccountError: t('add-account.error_invalid_region', {
              location: this.state.campaignLocation.toUpperCase(),
            }),
          });
          return {};
        }

        tokenType = 'phone_number';
        if (!token) throw new Error('Invalid phone number.');
      } catch (err) {
        this.setState({ addAccountError: t('add-account.error_invalid_input') });
        // Show the error
        return {};
      }
    }
    return { token, tokenType };
  }

  /** Called when the user wants to login in the AddAccount step */
  doLogin() {
    if (!this.state.loginToken || this.state.loginToken.length == 0) {
      this.setState({ addAccountError: t('add-account.error_invalid_input') });
      return;
    }
    const validated = this.validateToken();

    if (validated.token == null) return;

    const token = validated.token;
    const tokenType = validated.tokenType;

    // Show loader
    this.setState({ loadingText: t('add-account.loader_checking_account') });

    // Try login to blockv vatom network
    return BLOCKv.request('POST', '/v1/user/login', {
      token: token,
      token_type: tokenType,
      auth_data: {
        password: '',
      },
    })
      .then((e) => {
        // Hide loader
        this.setState({ loadingText: null });

        // Login successful: This is a misleading situation. The login was actually not successful, but the user
        // has no password or their account in unverified, and the server has sent the user an OTP to log in wit`h instead.
        this.setState({ loginTokenCleaned: token });
        this.gotoLoginOTPStep();
      })
      .catch((err) => {
        // Hide loader
        this.setState({ loadingText: null });

        console.log(err.code, err.message);

        // Check error code
        if (err.code == 2032) {
          // Standard account, go to the password step
          this.setState({ loginTokenCleaned: token });
          this.gotoLoginPasswordStep();
        } else if (err.code == 2030) {
          if (
            (this.state && this.state.registerSkipCapture) ||
            (this.state.registerDisableName &&
              this.state.registerDisablePassword &&
              this.state.registerDisableBirthday)
          )
            return this.doSkipRegister(token, tokenType);
          // Account does not exist, go to register step
          this.setState({
            step: Steps.CreateAccount,
            registerToken: token,
            registerTokenType: tokenType,
          });
        } else {
          // Some other error. We should show it to the user.
          Alert.notify(t('error.title'), err.message, t('error.action_confirm'));
        }
      });
  }

  /** In this step we ask the user to enter their password */
  gotoLoginPasswordStep() {
    // Go to step
    this.setState({ step: Steps.LoginPassword });
  }

  /** Called when the user wants to reset their password */
  doLoginForgotPassword() {
    // Show loading
    this.setState({ loadingText: t('login-password.loader_processing') });

    // Send request
    BLOCKv.request('POST', '/v1/user/reset_token', {
      token: this.state.loginTokenCleaned,
      token_type: this.state.loginTokenCleaned.indexOf('@') == -1 ? 'phone_number' : 'email',
    })
      .then((e) => {
        // Go to the OTP screen
        this.setState({ loadingText: null });
        this.gotoLoginOTPStep();
      })
      .catch((err) => {
        // Show error
        this.setState({ loadingText: null });
        Alert.notify(t('error.title'), err.message, t('error.action_confirm'));
      });
  }

  /** Performs the password login */
  doLoginWithPassword(password) {
    if (!password || password.length == 0) {
      this.setState({ loginPasswordError: t('login-password.error_empty_input') });
      return;
    }
    // Show loader
    this.setState({ loadingText: t('login-password.loader_logging_in') });

    // Try login
    BLOCKv.request('POST', '/v1/user/login', {
      token: this.state.loginTokenCleaned,
      token_type: this.state.loginTokenCleaned.indexOf('@') == -1 ? 'phone_number' : 'email',
      auth_data: {
        password,
      },
    })
      .then((e) => {
        // Remove loader
        this.setState({ loadingText: null });
        this.validateDetails(e.refresh_token.token, e.user);
      })
      .catch((err) => {
        this.setState({ loadingText: null });
        if (err.code == '2032') {
          this.setState({ loginPasswordError: t('login-password.error_incorrect_password') });
          return;
        }
        // Show error
        Alert.notify(t('error.title'), err.message, t('error.action_confirm'));
      });
  }

  validateDetails(refreshToken, user) {
    if (this.state.enforceDetails && user && !this.state.registerSkipCapture) {
      const firstName = user.properties.first_name;
      const lastName = user.properties.last_name;
      const birthday = user.properties.birthday;
      const passwordSet = user.properties.is_password_set;
      let show = false;

      if (!this.state.registerDisableName && (!firstName || firstName.length == 0)) show = true;
      if (!this.state.registerDisableName && (!lastName || lastName.length == 0)) show = true;
      if (!this.state.registerDisableBirthday && (!birthday || birthday.length == 0)) show = true;
      if (!this.state.registerDisablePassword && !passwordSet) show = true;

      if (show) {
        this.setState({
          loginFirstName: firstName,
          loginLastName: lastName,
          loginBirthday: birthday && birthday.length > 0 ? birthday : null,
          loginPasswordSet: passwordSet,
          step: Steps.UpdateAccount,
          refreshToken: refreshToken,
        });
        return;
      }
    }
    this.doSuccess(refreshToken);
  }

  /** In this step we ask the user to enter their OTP */
  gotoLoginOTPStep() {
    // Go to step
    this.setState({ step: Steps.LoginOTP });
  }

  /** Resend the OTP code */
  doLoginResendOTP() {
    // Show loading
    this.setState({ loadingText: t('login-otp.loader_resending_code') });

    // Send request
    BLOCKv.request('POST', '/v1/user/reset_token', {
      token: this.state.loginTokenCleaned.trim(),
      token_type: this.state.loginTokenCleaned.indexOf('@') == -1 ? 'phone_number' : 'email',
    })
      .then((e) => {
        // Go to the OTP screen
        this.setState({ loadingText: null });
      })
      .catch((err) => {
        // Show error
        this.setState({ loadingText: null });
        Alert.notify(t('error.title'), err.message, t('error.action_confirm'));
      });
  }

  /** Performs the OTP login */
  doLoginWithOTP(otp) {
    if (!otp || otp.length == 0) {
      this.setState({ loginOtpError: t('login-otp.error_empty_input') });
      return;
    }

    // Show loader
    this.setState({ loadingText: t('login-otp.loader_logging_in') });

    // Try login
    BLOCKv.request('POST', '/v1/user/login', {
      token: this.state.loginTokenCleaned,
      token_type: this.state.loginTokenCleaned.indexOf('@') == -1 ? 'phone_number' : 'email',
      auth_data: {
        password: otp,
      },
    })
      .then((e) => {
        // Login successfil
        this.setState({ loadingText: null });
        this.validateDetails(e.refresh_token.token, e.user);
      })
      .catch((err) => {
        this.setState({ loadingText: null });
        if (err.code === 2032) {
          this.setState({ loginOtpError: t('login-otp.error_invalid_pin') });
          return;
        }

        Alert.notify(t('error.title'), err.message, t('error.action_confirm'));
      });
  }

  /** Called when the user wants to cancel this auth request */
  cancelPermission() {
    // Return failure
    this.doFail('access_denied', 'The user declined the permission request.');
  }

  /** Called to trigger a fail on this auth request. */
  doFail(errorCode, errorText) {
    // If redirect URI is not found, show error on the screen instead
    if (!this.redirectURI || this.redirectURI.indexOf(':') == -1)
      return this.setState({ loadingText: errorText });

    // Do redirect
    this.doRedirect({
      error: errorCode,
      error_text: errorText,
      state: this.customState,
    });
  }

  /**
   *  Called to trigger a success on this auth request.
   *
   *  @param {string} refreshToken The target user's refresh token *in the oauth web app's context*.
   */
  async doSuccess(refreshToken) {
    try {
      // Show action
      this.setState({ loadingText: t('general.loader_please_wait') });

      const accessToken = await BLOCKv.getAccessToken(refreshToken);
      // validate redirect url
      await BLOCKv.request(
        'GET',
        '/v1/publisher/app/validate?redirect_url=' + encodeURI(this.redirectURI),
        null,
        { Authorization: 'Bearer ' + accessToken }
      );

      const params = getAllUrlParams();
      supportedParams.forEach((param) => {
        delete params[param];
      });
      // Send it to the app
      this.doRedirect({
        ...params,
        refresh_token: refreshToken,
        state: this.customState,
        flow: this.state && this.state.flow,
      });
    } catch (err) {
      // Show error
      this.setState({ loadingText: null });
      Alert.notify(t('error.title'), err.message, t('error.action_confirm'));
    }
  }

  /**
   * Redirect back to the app.
   *
   * @param {object} params The redirect params to pass back to the app.
   */
  doRedirect(params) {
    // Show action
    this.setState({ loadingText: t('general.loader_redirecting') });
    // Create redirect url
    let fields = Object.keys(params).map((key) => key + '=' + encodeURIComponent(params[key]));

    window.location.href = this.redirectURI + '?' + fields.join('&');
  }
}
